/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Chips } from 'primereact/chips';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { AntFlowServerError, AntflowApiEndpoint } from '@/services/dataprovider';
import { Launcher } from '@/types/models';
import { UserGroupsBanner } from './UserGroupsBanner';

interface EditLauncherDialogProps {
  visible: boolean;
  launcher: Launcher;
  onHide: () => void;
  onSuccess: () => void;
  apiEndpoint: AntflowApiEndpoint;
}

export const EditLauncherDialog: React.FC<EditLauncherDialogProps> = ({
  visible,
  launcher,
  onHide,
  onSuccess,
  apiEndpoint
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [groups, setGroups] = useState<string[]>([]);
  const [error, setError] = useState<string | null>();

  useEffect(() => {
    if (launcher) {
      setName(launcher.name);
      setGroups(launcher.groups ?? []);
    }
  }, [launcher]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!launcher) {
        return;
      }
      return apiEndpoint.update_launcher(launcher.id, { name, groups });
    },
    onSuccess: () => {
      onSuccess();
      handleClose();
    },
    onError(error: AntFlowServerError) {
      const errorData = error.response?.data as Record<string, string>;
      if (typeof errorData === 'object' && errorData !== null) {
        setError(errorData.detail);
      }
    }
  });

  const handleClose = () => {
    setName('');
    setGroups([]);
    setError(null);
    updateMutation.reset();
    onHide();
  };

  const handleSubmit = () => {
    if (name.trim()) {
      updateMutation.mutate();
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label={t(['actions.cancel', 'Cancel'])}
        severity="secondary"
        size="small"
        onClick={handleClose}
        disabled={updateMutation.isPending}
      />
      <Button
        label={t(['launchers.update', 'Update'])}
        icon="pi pi-check"
        size="small"
        onClick={handleSubmit}
        disabled={!name.trim() || updateMutation.isPending}
        loading={updateMutation.isPending}
      />
    </div>
  );

  return (
    <Dialog
      header={t(['launchers.edit_title', 'Edit Launcher'])}
      visible={visible}
      style={{ width: '30%' }}
      onHide={handleClose}
      footer={footer}
      closable={!updateMutation.isPending}
    >
      <div className="flex flex-column gap-3">
        <UserGroupsBanner className="border-round surface-100 p-3" />

        <div className="flex flex-column gap-2">
          <label htmlFor="edit-launcher-name" className="font-medium">
            {t(['launchers.name', 'Name'])} *
          </label>
          <InputText
            id="edit-launcher-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={updateMutation.isPending}
          />
        </div>

        {launcher.user_id == null && (
          <div className="flex flex-column gap-2">
            <label htmlFor="edit-launcher-groups" className="font-medium">
              {t(['launchers.groups', 'Groups'])}
            </label>
            <Chips
              id="edit-launcher-groups"
              value={groups} // Ensure it's an array of strings
              onChange={(e) => setGroups(e.value ?? [])}
              className="w-full"
              pt={{
                container: { className: 'w-full' }
              }}
              placeholder={t(['launchers.groups_placeholder', 'Type and press Enter to add'])}
              disabled={updateMutation.isPending}
            />
          </div>
        )}

        {updateMutation.isError && (
          <div className="p-message p-message-error p-3 border-round">
            <i className="pi pi-times-circle mr-2" />
            {error || t(['notifs.error.description', 'An error occurred'])}
          </div>
        )}
      </div>
    </Dialog>
  );
};
